import { ReactNode, useMemo } from 'react';
import BigNumber from 'bignumber.js';

import SETTINGS from '#/config/global-settings';

import createSafeContext from '#/utils/createSafeContext';

import { useOpenPositionsView } from '#/features/perpetuals/open-positions-context';
import { usePerpetualMarketsStoreView } from '#/features/perpetuals/market-info/markets-context';
import { useMarketsSummaryStoreView } from '#/features/perpetuals/market-info/markets-summary-context';
import * as PositionFns from '#/features/perpetuals/trade-overview/position-functions';

import { OpenPosition } from '#/api/positions';

import { OpenOrder } from '#/api/orders';
import { Market } from '#/api/markets';
import { MarketSummary } from '#/api/markets-summary';
import { Account } from '#/api/account';
import { calcOpenNotional } from '#/features/perpetuals/trade-overview/position-functions';
import { useOpenOrdersView } from '#/features/perpetuals/trade-overview/open-orders-context';

import { AccountState, useAccountState } from './account-context';
import {
  accountValueView,
  freeCollateralView,
  healthFactorView,
  totalCollateralView,
} from './account-views';

export const [useAccountSummaryView, AccountSummaryViewCtx] =
  createSafeContext<AccountSummaryView>('AccountSummaryView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function AccountSummaryViewProvider(props: ProviderProps) {
  const accountState = useAccountState();
  const openOrdersView = useOpenOrdersView();
  const openOrders = openOrdersView.getOrders();
  const openPositionsView = useOpenPositionsView();
  const openPositions = openPositionsView.getPositions();
  const marketsStoreView = usePerpetualMarketsStoreView();
  const marketsSummaryStoreView = useMarketsSummaryStoreView();

  const view = useMemo(
    () =>
      createView(
        accountState,
        openOrders,
        openPositions,
        marketsStoreView.getMarket,
        marketsSummaryStoreView.getMarketSummary,
      ),
    [
      accountState,
      openOrders,
      openPositions,
      marketsStoreView.getMarket,
      marketsSummaryStoreView.getMarketSummary,
    ],
  );

  return (
    <AccountSummaryViewCtx value={view}>{props.children}</AccountSummaryViewCtx>
  );
}

export interface AccountSummaryView {
  readonly totalCollateral: string;
  readonly freeCollateral: string;
  readonly accountValue: string;
  readonly unrealizedPnl: string;
  readonly unrealizedPnlDirection: 'up' | 'down';
  readonly positionNotional: string;

  // Derived:
  readonly healthFactor: string;
  readonly leverage: string;
}

const emptyView: AccountSummaryView = {
  totalCollateral: '-',
  freeCollateral: '-',
  accountValue: '-',
  unrealizedPnl: '-',
  unrealizedPnlDirection: 'up',
  positionNotional: '-',
  healthFactor: '-',
  leverage: '-',
};

export function createView(
  state: AccountState,
  openOrders: readonly OpenOrder[],
  openPositions: readonly OpenPosition[],
  getMarket: (market: string) => Market | undefined,
  getMarketSummary: (market: string) => MarketSummary | undefined,
): AccountSummaryView {
  const { account } = state;

  if (account == null) return emptyView;

  const totalCollateral = totalCollateralView(account);

  const freeCollateral = freeCollateralView(account);

  const accountValue = accountValueView(account);

  const { unrealizedPnl, unrealizedPnlDirection } =
    unrealizedPnlView(openPositions);

  const positionNotional = positionNotionalView(
    openPositions,
    getMarket,
    getMarketSummary,
  );

  const healthFactor = healthFactorView(account);

  const leverage = leverageView(
    account,
    openOrders,
    openPositions,
    getMarket,
    getMarketSummary,
  );

  return {
    totalCollateral,
    freeCollateral,
    accountValue,
    unrealizedPnl,
    unrealizedPnlDirection,
    positionNotional,
    healthFactor,
    leverage,
  };
}

function unrealizedPnlView(openPositions: readonly OpenPosition[]) {
  const unrealizedPnlValue = openPositions.reduce((acc, position) => {
    return acc.plus(position.unrealized_pnl);
  }, BigNumber('0'));

  const unrealizedPnl = `${
    unrealizedPnlValue.isPositive() ? '+' : '-'
  }$${unrealizedPnlValue.abs().toFormat(2)}`;

  const unrealizedPnlDirection = unrealizedPnlValue.isPositive()
    ? ('up' as const)
    : ('down' as const);

  return { unrealizedPnl, unrealizedPnlDirection };
}

function positionNotionalView(
  openPositions: readonly OpenPosition[],
  getMarket: (market: string) => Market | undefined,
  getMarketSummary: (market: string) => MarketSummary | undefined,
) {
  const positionNotional = PositionFns.calcPositionNotional(
    openPositions,
    getMarket,
    getMarketSummary,
  );

  if (!positionNotional.isFinite()) return '-';

  return `$${positionNotional.toFormat(SETTINGS.fiat.USD.scale)}`;
}

function leverageView(
  account: Account,
  openOrders: readonly OpenOrder[],
  openPositions: readonly OpenPosition[],
  getMarket: (market: string) => Market | undefined,
  getMarketSummary: (market: string) => MarketSummary | undefined,
) {
  const format = (leverage: BigNumber) =>
    !leverage.isFinite() ? '-' : `${leverage.toFormat(1)}x`;

  const openNotional = calcOpenNotional(
    openOrders,
    openPositions,
    getMarket,
    getMarketSummary,
  );

  const leverage = openNotional.dividedBy(account.account_value);
  const maxLeverage = openNotional.dividedBy(
    account.initial_margin_requirement,
  );

  return `${format(leverage)} / ${format(maxLeverage)}`;
}
